import gql from "graphql-tag";
import { VALUE_LIST_FRAGMENT } from "./valuelist-gql";

export const ADDRESS_USAGE_FRAGMENT = gql`
    fragment addressUsageFragment on AddressUsage {
        id
        addressId
        postalCode
        city
        addressTxt
        addressTxt2
        primary
        mailing
        businessEntityId
        unitDesignator {
            id
        }
        unitNumber
        unitNumberFrench
        unitNumberFrenchSync
        floorOrLevel
        levelNumberFrench
        levelNumberFrenchSync
        additionalInfo
        additionalInfoFrench
        additionalInfoFrenchSync
        openingTime {
            id
        }
        closingTime {
            id
        }
        operationalInfo
        operationalInfoFrench
        operationalInfoFrenchSync
        officeLanguage {
            id
        }
    }
`;

export const CONTACT_PHONE_FRAGMENT = gql`
    ${VALUE_LIST_FRAGMENT}
    fragment contactPhoneFragment on ContactPhone {
        id
        phoneType {
            ...valueListFragment
        }
        assignType {
            ...valueListFragment
        }
        businessEntityId
        countryCode
        areaCode
        localNumber
        extension
        description
        descriptionFrench
        descriptionFrenchSync
    }
`;


export const DIGITAL_ADDRESS_FRAGMENT = gql`
    ${VALUE_LIST_FRAGMENT}
    fragment digitalAddressFragment on DigitalAddress {
        id
        businessEntityId
        description
        descriptionFrench
        descriptionFrenchSync
        netAddressTxt
        emailAddressTxt
        digitalType {
            ...valueListFragment
        }
        assignType {
            ...valueListFragment
        }
    }
`;

export const SEARCH_ADDRESS = gql`
    query SearchAddress($q:String!,$limit:Int,$includeInactive:Boolean){
        searchAddress(q:$q,limit:$limit,includeInactive:$includeInactive){
            id
            addressTxt
            addressTxt2
        }
    }
`;

//--------------------------------------------------------------------------------------
// Org
//--------------------------------------------------------------------------------------

export const GET_ORG_ADDRESSES = gql`
    ${ADDRESS_USAGE_FRAGMENT}
    query Org($orgId: ID!) {
        org (orgId: $orgId) {
            id
            addressUsages {
                ...addressUsageFragment
            }
        }
    }
`;

export const GET_ORG_DIGITAL_ADDRESSES =  gql`
    ${DIGITAL_ADDRESS_FRAGMENT}
    query Org($orgId: ID!) {
        org (orgId: $orgId) {
            id
            digitalAddresses {
               ...digitalAddressFragment
            }
        }
    }
`;

export const GET_ORG_CONTACT_PHONES = gql`
    ${CONTACT_PHONE_FRAGMENT}
    query Org($orgId: ID!) {
        org (orgId: $orgId) {
            id
            contactPhones {
              ...contactPhoneFragment
            }
        }
    }
`;

export const CREATE_ORG_ADDRESS = gql`
    mutation CreateOrgAddress($orgId:ID!,$address:AddressUsageInput!) {
        createOrgAddress(orgId:$orgId,address:$address){
            id
        }
    }
`;

export const UPDATE_ORG_ADDRESS = gql`
    mutation UpdateOrgAddress($orgId:ID!, $address:AddressUsageInput!) {
        updateOrgAddress(orgId:$orgId, address:$address){
            id
        }
    }
`;

export const SET_ORG_PRIMARY_ADDRESS = gql`
    mutation SetOrgPrimaryAddress($orgId:ID!, $addressUsageId: ID!) {
        setOrgPrimaryAddress(orgId:$orgId, addressUsageId: $addressUsageId){
            id
        }
    }
`;

export const SET_ORG_MAILING_ADDRESS = gql`
    mutation SetOrgMailingAddress($orgId:ID!, $addressUsageId: ID!) {
        setOrgMailingAddress(orgId:$orgId, addressUsageId: $addressUsageId){
            id
        }
    }
`;

export const DELETE_ORG_ADDRESSES = gql`
    mutation DeleteOrgAddresses($orgId: ID!, $addressUsageIds: [ID!]) {
        deleteOrgAddresses(orgId: $orgId, addressUsageIds: $addressUsageIds)
    }
`;

export const CREATE_ORG_CONTACT_PHONE = gql`
    mutation CreateOrgContactPhone($orgId: ID!, $input: ContactPhoneInput!) {
        createOrgContactPhone(orgId: $orgId, input: $input) {
            id
        }
    }
`;

export const UPDATE_ORG_CONTACT_PHONE = gql`
    mutation UpdateOrgContactPhone($orgId: ID!, $input: ContactPhoneInput!) {
        updateOrgContactPhone(orgId: $orgId, input: $input) {
            id
        }
    }
`;

export const DELETE_ORG_CONTACT_PHONES = gql`
    mutation DeleteOrgContactPhones($orgId: ID!, $contactPhoneIds: [ID!]) {
        deleteOrgContactPhones(orgId: $orgId, contactPhoneIds: $contactPhoneIds)
    }
`;

export const CREATE_ORG_DIGITAL_ADDRESS = gql`
    mutation createOrgDigitalAddress($orgId: ID!, $input: DigitalAddressInput!) {
        createOrgDigitalAddress(orgId: $orgId, input: $input) {
            id
        }
    }
`;

export const UPDATE_ORG_DIGITAL_ADDRESS = gql`
    mutation UpdateOrgDigitalAddress($orgId: ID!, $input: DigitalAddressInput!) {
        updateOrgDigitalAddress(orgId: $orgId, input: $input) {
            id
        }
    }
`;

export const DELETE_ORG_DIGITAL_ADDRESSES = gql`
    mutation DeleteOrgDigitalAddresses($orgId: ID!,$digitalAddressIds: [ID!]) {
        deleteOrgDigitalAddresses(orgId: $orgId, digitalAddressIds: $digitalAddressIds)
    }
`;

export const SET_ORG_PRIMARY_DIGITAL_ADDRESS = gql`
    mutation SetOrgPrimaryDigitalAddress($orgId: ID!, $digitalAddressId: ID!) {
        setOrgPrimaryDigitalAddress(orgId: $orgId, digitalAddressId: $digitalAddressId){
            id
        }
    }
`;

//--------------------------------------------------------------------------------------
// Position
//--------------------------------------------------------------------------------------

export const GET_POSITION_ADDRESSES = gql`
    ${ADDRESS_USAGE_FRAGMENT}
    query Position($positionId: ID!) {
        position(positionId: $positionId) {
            positionId
            addressUsages {
                ...addressUsageFragment,
                orgAddress
            }
        }
    }
`;

export const GET_POSITION_EMAILS =  gql`
    ${DIGITAL_ADDRESS_FRAGMENT}
    query Position($positionId: ID!) {
        position(positionId: $positionId) {
            positionId
            digitalAddresses {
                ...digitalAddressFragment
            }
        }
    }
`;

export const GET_POSITION_CONTACT_PHONES = gql`
    ${CONTACT_PHONE_FRAGMENT}
    query Position($positionId: ID!) {
        position(positionId: $positionId) {
            positionId
            contactPhones {
                ...contactPhoneFragment
            }
        }
    }
`;

export const CREATE_POSITION_ADDRESS = gql`
    mutation CreatePositionAddress($positionId:ID!,$address:AddressUsageInput!) {
        createPositionAddress(positionId:$positionId,address:$address){
            id
        }
    }
`;

export const UPDATE_POSITION_ADDRESS = gql`
    mutation UpdatePositionAddress($positionId:ID!, $address:AddressUsageInput!) {
        updatePositionAddress(positionId:$positionId, address:$address){
            id
        }
    }
`;

export const SET_POSITION_PRIMARY_ADDRESS = gql`
    mutation SetPositionPrimaryAddress($positionId:ID!, $addressUsageId: ID!) {
        setPositionPrimaryAddress(positionId:$positionId, addressUsageId: $addressUsageId){
            id
        }
    }
`;

export const SET_POSITION_MAILING_ADDRESS = gql`
    mutation SetPositionMailingAddress($positionId:ID!, $addressUsageId: ID!) {
        setPositionMailingAddress(positionId:$positionId, addressUsageId: $addressUsageId){
            id
        }
    }
`;

export const UNSET_POSITION_PRIMARY_ADDRESS = gql`
    mutation UnsetPositionPrimaryAddress($positionId:ID!, $addressUsageId: ID!) {
        unsetPositionPrimaryAddress(positionId:$positionId, addressUsageId: $addressUsageId){
            id
        }
    }
`;

export const UNSET_POSITION_MAILING_ADDRESS = gql`
    mutation UnsetPositionMailingAddress($positionId:ID!, $addressUsageId: ID!) {
        unsetPositionMailingAddress(positionId:$positionId, addressUsageId: $addressUsageId){
            id
        }
    }
`;

export const DELETE_POSITION_ADDRESSES = gql`
    mutation DeletePositionAddresses($positionId:ID!, $addressUsageIds: [ID!]) {
        deletePositionAddresses(positionId:$positionId, addressUsageIds: $addressUsageIds)
    }
`;

export const CREATE_POSITION_CONTACT_PHONE = gql`
    mutation CreatePositionContactPhone($positionId: ID!, $input: ContactPhoneInput!) {
        createPositionContactPhone(positionId: $positionId, input: $input) {
            id
        }
    }
`;

export const UPDATE_POSITION_CONTACT_PHONE = gql`
    mutation UpdatePositionContactPhone($positionId: ID!, $input: ContactPhoneInput!) {
        updatePositionContactPhone(positionId: $positionId, input: $input) {
            id
        }
    }
`;

export const DELETE_POSITION_CONTACT_PHONES = gql`
    mutation DeletePositionContactPhones($positionId: ID!, $contactPhoneIds: [ID!]) {
        deletePositionContactPhones(positionId:$positionId, contactPhoneIds: $contactPhoneIds)
    }
`;

export const SET_POSITION_PRIMARY_PHONE = gql`
    mutation SetPositionPrimaryPhone($positionId: ID!, $contactPhoneId: ID!) {
        setPositionPrimaryPhone(positionId: $positionId, contactPhoneId: $contactPhoneId){
            id
        }
    }
`;

export const CREATE_POSITION_EMAIL = gql`
    mutation createPositionEmail($positionId: ID!, $input: EmailInput!) {
        createPositionEmail(positionId: $positionId, input: $input) {
            id
        }
    }
`;

export const UPDATE_POSITION_EMAIL = gql`
    mutation UpdatePositionEmail($positionId: ID!, $input: EmailInput!) {
        updatePositionEmail(positionId: $positionId, input: $input) {
            id
        }
    }
`;

export const DELETE_POSITION_EMAILS = gql`
    mutation DeletePositionEmails($positionId: ID!, $digitalAddressIds: [ID!]) {
        deletePositionEmails(positionId: $positionId, digitalAddressIds: $digitalAddressIds)
    }
`;

export const SET_POSITION_PRIMARY_EMAIL = gql`
    mutation setPositionPrimaryEmail($positionId: ID!, $digitalAddressId: ID!) {
        setPositionPrimaryEmail(positionId: $positionId, digitalAddressId: $digitalAddressId){
            id
        }
    }
`;

export const CREATE_INDIVIDUAL_EMAIL = gql`
    mutation createIndividualEmail($individualId: ID!, $input: DigitalAddressInput!) {
        createIndividualEmail(individualId: $individualId, input: $input) {
            id
        }
    }
`;

export const DELETE_INDIVIDUAL_EMAILS = gql`
    mutation DeleteIndividualEmails($individualId: ID!, $digitalAddressIds: [ID!]) {
        deleteIndividualEmails(individualId: $individualId, digitalAddressIds: $digitalAddressIds)
    }
`;

export const UPDATE_INDIVIDUAL_EMAIL = gql`
    mutation UpdateIndividualEmail($individualId: ID!, $input: DigitalAddressInput!) {
        updateIndividualEmail(individualId: $individualId, input: $input) {
            id
        }
    }
`;


export const GET_INDIVIDUAL_EMAILS =  gql`
    ${DIGITAL_ADDRESS_FRAGMENT}
    query Individual($individualId: ID!) {
        individual(individualId: $individualId) {
            individualId
            digitalAddresses {
                ...digitalAddressFragment
            }
        }
    }
`;

export const SET_INDIVIDUAL_PRIMARY_EMAIL = gql`
    mutation setIndividualPrimaryEmail($individualId: ID!, $digitalAddressId: ID!) {
        setIndividualPrimaryEmail(individualId: $individualId, digitalAddressId: $digitalAddressId){
            id
        }
    }
`;


